const PREFIX = "gudwin:person-create-draft:v2:";
const LEGACY_PREFIX = "gudwin:person-create-draft:v1:";
const VERSION = 2;
const TTL_MS = 24 * 60 * 60 * 1000;

export interface PersonCreateDraftPayload {
  group?: "office" | "worker";
  last_name?: string;
  first_name?: string;
  patronymic?: string;
  role?: "admin" | "viewer" | "office_staff" | "worker";
  web_email?: string;
  tab_number?: string;
  unit_id?: number | null;
  position_id?: number | null;
  calendar_id?: number | null;
  tracks_time?: boolean;
  is_project_manager?: boolean;
  hire_date?: string | null;
  termination_date?: string | null;
}

export interface PersonDraftCatalogs {
  unitIds: ReadonlySet<number>;
  positionIds: ReadonlySet<number>;
  calendarIds: ReadonlySet<number>;
}

interface Envelope {
  version: 2;
  actor_id: number;
  saved_at: number;
  payload: PersonCreateDraftPayload;
}

export function personCreateDraftKey(actorId: number): string {
  return `${PREFIX}${actorId}`;
}

function cleanForeignKeys(actorId: number): void {
  const own = personCreateDraftKey(actorId);
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX) && key !== own) localStorage.removeItem(key);
    if (key?.startsWith(LEGACY_PREFIX)) localStorage.removeItem(key);
  }
}

export function savePersonCreateDraft(actorId: number, source: PersonCreateDraftPayload): void {
  cleanForeignKeys(actorId);
  const payload: PersonCreateDraftPayload = {
    group: source.group,
    last_name: source.last_name,
    first_name: source.first_name,
    patronymic: source.patronymic,
    role: source.role,
    web_email: source.web_email,
    tab_number: source.tab_number,
    unit_id: source.unit_id,
    position_id: source.position_id,
    calendar_id: source.calendar_id,
    tracks_time: source.tracks_time,
    is_project_manager: source.is_project_manager,
    hire_date: source.hire_date,
    termination_date: source.termination_date,
  };
  const envelope: Envelope = { version: VERSION, actor_id: actorId, saved_at: Date.now(), payload };
  localStorage.setItem(personCreateDraftKey(actorId), JSON.stringify(envelope));
}

export function loadPersonCreateDraft(
  actorId: number,
  catalogs: PersonDraftCatalogs,
): PersonCreateDraftPayload | null {
  cleanForeignKeys(actorId);
  const key = personCreateDraftKey(actorId);
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as Partial<Envelope>;
    if (
      envelope.version !== VERSION ||
      envelope.actor_id !== actorId ||
      typeof envelope.saved_at !== "number" ||
      Date.now() - envelope.saved_at > TTL_MS ||
      !envelope.payload ||
      typeof envelope.payload !== "object"
    ) {
      throw new Error("invalid draft");
    }
    const payload = envelope.payload;
    return {
      group: payload.group,
      last_name: typeof payload.last_name === "string" ? payload.last_name : undefined,
      first_name: typeof payload.first_name === "string" ? payload.first_name : undefined,
      patronymic: typeof payload.patronymic === "string" ? payload.patronymic : undefined,
      role: payload.role,
      web_email: typeof payload.web_email === "string" ? payload.web_email : undefined,
      tab_number: typeof payload.tab_number === "string" ? payload.tab_number : undefined,
      unit_id:
        typeof payload.unit_id === "number" && catalogs.unitIds.has(payload.unit_id)
          ? payload.unit_id
          : null,
      position_id:
        typeof payload.position_id === "number" && catalogs.positionIds.has(payload.position_id)
          ? payload.position_id
          : null,
      calendar_id:
        typeof payload.calendar_id === "number" && catalogs.calendarIds.has(payload.calendar_id)
          ? payload.calendar_id
          : null,
      tracks_time: payload.tracks_time === true,
      is_project_manager: payload.is_project_manager === true,
      hire_date: typeof payload.hire_date === "string" ? payload.hire_date : null,
      termination_date:
        typeof payload.termination_date === "string" ? payload.termination_date : null,
    };
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

export function clearPersonCreateDraft(actorId: number): void {
  localStorage.removeItem(personCreateDraftKey(actorId));
}

export function clearAllPersonCreateDrafts(): void {
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) localStorage.removeItem(key);
    if (key?.startsWith(LEGACY_PREFIX)) localStorage.removeItem(key);
  }
}
